import { motion } from "framer-motion";
import HappyPanda from "../../assets/images/Panda/MagicPanda1.png";
import ResponsiveImage from "../common/ResponsiveImage";
import {
    Heart,
    Users,
    Lightbulb,
    Shield,
    Zap,
    CheckCircle,
} from "lucide-react";
import React, { useEffect, useState } from 'react';

const MotionResponsiveImage = motion(ResponsiveImage);

// Danh sách điểm mạnh (không kèm vị trí – sẽ được tính động theo quỹ đạo)
const strengths = [
    { title: "Đội ngũ chuyên nghiệp", desc: "50+ chuyên gia đa lĩnh vực, quy trình rõ ràng.", icon: Users },
    { title: "Công nghệ hiện đại", desc: "React, Next.js, Microservices, Cloud, AI.", icon: Lightbulb },
    { title: "Bảo mật & Tin cậy", desc: "Áp dụng chuẩn OWASP, mã hoá & sao lưu định kỳ.", icon: Shield },
    { title: "Tốc độ triển khai", desc: "Agile linh hoạt – ra mắt MVP nhanh chóng.", icon: Zap },
    { title: "Quy trình chuẩn", desc: "Chuẩn hoá từ phân tích → vận hành → tối ưu.", icon: CheckCircle },
    { title: "Hỗ trợ 24/7", desc: "Đồng hành & phản hồi nhanh mọi kênh.", icon: Heart },
];

const SectionStrong: React.FC = () => {
    return (
        <>
            <section className="py-5 mb-20 relative overflow-hidden bg-gradient-to-b from-white via-indigo-50/40 to-white">
                {/* Blurred gradient blobs */}
                <div className="pointer-events-none select-none absolute inset-0 opacity-50">
                    <div className="absolute -top-32 left-10 w-96 h-96 bg-gradient-to-br from-indigo-300/40 via-purple-300/30 to-pink-300/30 blur-3xl rounded-full animate-pulse"></div>
                    <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-tr from-blue-300/40 via-indigo-300/30 to-purple-300/30 blur-3xl rounded-full animate-blob animation-delay-2000"></div>
                </div>

                <div className="relative z-10 container mx-auto px-4">
                    <div className="text-center">
                        <div className="text-center">
                            <div className="text-4xl font-bold color-third p-3 rounded-xl ">
                               Thế mạnh cốt lỗi của chúng tôi

                            </div>
                        </div>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Những năng lực tạo nên khác biệt của TrioCore Solutions – xoay quanh giá trị khách hàng.
                        </p>
                    </div>

                    <OrbitStrengths />

                    {/* <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:hidden gap-6">
                        {strengths.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: i * 0.1 }}
                                    className="p-5 rounded-2xl bg-white/90 backdrop-blur border border-indigo-100 shadow-sm hover:shadow-xl transition-all"
                                >
                                    <div className="flex items-center gap-4 mb-2">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md"><Icon size={26} className="text-white"  /></div>
                                        <h4 className="font-semibold text-gray-800">{s.title}</h4>
                                    </div>
                                    <p className="text-sm text-gray-600 leading-relaxed pl-16 -mt-2">{s.desc}</p>
                                </motion.div>
                            )
                        })}
                    </div> */}
                </div>
            </section>

            {/* CTA Section */}

        </>
    )
}


export default SectionStrong;

// Component tách riêng để xử lý quỹ đạo 3D + z-index động
const OrbitStrengths: React.FC = () => {
    const [angle, setAngle] = useState(0); // góc tổng (radians)
    const [paused, setPaused] = useState(false);
    const speed = 0.008; // tốc độ quay
    const radius = 460; // bán kính lớn hơn như yêu cầu

    useEffect(() => {
        let raf: number;
        const loop = () => {
            if (!paused) {
                setAngle(a => (a + speed) % (Math.PI * 2));
            }
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(raf);
    }, [paused]);

    return (
        <div
            className="relative mx-auto flex items-center justify-center select-none"
            style={{ perspective: '1800px', height: '560px' }}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            {/* Panda center */}
            <motion.div
                className="relative"
                style={{ transformStyle: 'preserve-3d', zIndex: 350 }}

                animate={{ rotateY: [-8, 8, -8], rotateX: [2, -2, 2] }}
                transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
                viewport={{ once: true }}
            >
                <div className="relative w-[340px] h-[340px]" style={{ transformStyle: 'preserve-3d' }}>
                    {/* Glow field */}
                    <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-indigo-500/40 via-purple-500/30 to-pink-500/30 blur-3xl animate-pulse" />
                    {/* Faux depth back-face */}
                    <div
                        className="absolute inset-0"
                        style={{
                            transform: 'translateZ(-40px) scale(0.92)',
                            filter: 'blur(8px) brightness(0.85)',
                            opacity: 0.35,
                            background: 'radial-gradient(circle at 60% 40%, rgba(255,255,255,0.4), rgba(255,255,255,0))'
                        }}
                    />
                    {/* Main image layer */}
                    <MotionResponsiveImage
                        src={HappyPanda}
                        alt="TrioCore Solutions Mascot"
                        className="relative w-full h-full object-contain drop-shadow-2xl"
                        style={{ transform: 'translateZ(25px)' }}
                        animate={{ y: [0, -18, 0] }}
                        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                    />
                    {/* Front subtle highlight */}
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            transform: 'translateZ(55px)',
                            background: 'radial-gradient(circle at 50% 30%, rgba(255,255,255,0.35), rgba(255,255,255,0) 60%)',
                            mixBlendMode: 'overlay',
                            opacity: 0.7
                        }}
                    />
                </div>
            </motion.div>

            {/* Decorative orbit guides */}
            <div className="absolute rounded-full border border-dashed border-indigo-400/50" style={{ width: radius * 2 + 'px', height: radius * 2 + 'px' }} />
            <div className="absolute rounded-full border border-dashed border-purple-400/70" style={{ width: radius * 2.4 + 'px', height: radius * 2.4 + 'px' }} />

            {strengths.map((s, i) => {
                const Icon = s.icon;
                const portion = (2 * Math.PI) / strengths.length;
                // Góc riêng của item
                const a = angle + i * portion;
                // Quỹ đạo hình elip nhẹ (tạo cảm giác 3D nghiêng)
                const tilt = 18 * (Math.PI / 180); // 18deg
                const x = Math.cos(a) * radius;
                const zRaw = Math.sin(a) * radius; // dùng phân biệt trước / sau
                const y = Math.sin(a * 2) * 60; // dao động nhẹ theo chu kỳ
                // Nghiêng mặt phẳng quỹ đạo -> biến đổi y theo z
                const yTilted = y + Math.sin(tilt) * zRaw * 0.25;
                const z = Math.cos(tilt) * zRaw; // giá trị sâu

                // scale & blur theo chiều sâu
                const depthNorm = (z + radius) / (2 * radius); // 0 (xa) -> 1 (gần)
                const scale = 0.75 + depthNorm * 0.45; // lớn hơn khi tiến ra trước
                const brightness = 0.4 + depthNorm * 0.6; // dùng cho opacity glow
                const zIndex = 100 + Math.round(depthNorm * 500); // mở rộng để vượt qua panda khi ở trước

                return (
                    <motion.div
                        key={i}
                        className="absolute will-change-transform"
                        style={{
                            transform: `translate3d(${x}px, ${yTilted}px, ${z}px) scale(${scale})`,
                            transformStyle: 'preserve-3d',
                            zIndex,
                            filter: `brightness(${0.85 + depthNorm * 0.15}) saturate(${0.9 + depthNorm * 0.3})`,
                            transition: 'transform 0.4s ease-out'
                        }}
                    >
                        <motion.div
                            whileHover={{ scale: 1.12, translateZ: 40 }}
                            className="group relative w-64 rounded-2xl bg-white/65 backdrop-blur-xl p-5 border border-white/50 shadow-[0_8px_30px_rgba(31,38,135,0.15)] hover:shadow-[0_30px_60px_-10px_rgba(59,130,246,0.35)] transition-all"
                            style={{ transformStyle: 'preserve-3d' }}
                        >
                            {/* depth glow */}
                            <div
                                className="pointer-events-none absolute -inset-[2px] rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"
                                style={{
                                    background: `radial-gradient(circle at 30% 30%, rgba(99,102,241,${0.15 * brightness}), rgba(139,92,246,0) 70%)`
                                }}
                            />
                            <div className="relative flex flex-col items-center text-center gap-3">
                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/30 group-hover:shadow-purple-600/40 transition-all">
                                    <Icon size={32}  className="text-white" />
                                </div>
                                <h4 className="font-semibold text-gray-800 tracking-tight">{s.title}</h4>
                                <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
                            </div>
                            <div className="pointer-events-none absolute inset-0 rounded-2xl overflow-hidden">
                                <div className="absolute -top-1/2 left-0 w-full h-full bg-gradient-to-b from-white/40 to-transparent opacity-0 group-hover:opacity-60 rotate-12 transition-opacity" />
                            </div>
                        </motion.div>
                    </motion.div>
                );
            })}
        </div>
    );
};

